// A post, on the blog or in the resources. As with a chapter, the published
// page is the editor: Edit swaps the title and the body for their markdown
// where they stand.
//
// A post quotes the ESV with a line of its own — `{{Nehemiah 1:5-11}}` — and
// the words are fetched from the ESV as the post is read, so the post never
// holds them. The copyright line follows any post that quotes. A ```chart
// block is drawn where it stands (charts/Chart.tsx).
import {
  ESV_COPYRIGHT,
  openSections,
  quotedRefs,
  sectionPath,
  type CommentaryStatus,
  type PostInput,
  type PostSection,
  type PostView,
} from "@bibleunleashed/shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Redirect, useLocation, useParams } from "wouter";
import { Editable } from "@/commentary/Editable";
import { AdminBar } from "@/components/AdminBar";
import { Field } from "@/components/Field";
import { Header } from "@/components/Header";
import { TagsInput } from "@/components/TagsInput";
import { useUser } from "@/lib/auth";
import { postDate, SECTIONS, useDeletePost, usePost, useSavePost } from "@/lib/posts";
import { PostBody } from "./PostBody";
import { PreviewImageField } from "./PreviewImageField";

/** /blog/new and /resources/new are a post being started: it has no address until it is saved. */
const NEW = "new";

const blank = (section: PostSection): PostInput => ({
  section,
  slug: "",
  title: "",
  excerpt: "",
  bodyMd: "",
  tags: [],
  image: null,
  status: "draft",
});

const toInput = (post: PostView): PostInput => ({
  section: post.section,
  slug: post.slug,
  title: post.title,
  excerpt: post.excerpt,
  bodyMd: post.bodyMd,
  tags: post.tags,
  image: post.image,
  status: post.status,
});

export function PostPage({ section }: { section: PostSection }) {
  const { name, noun } = SECTIONS[section];
  const home = sectionPath(section);
  const { slug } = useParams<{ slug: string }>();
  const isNew = slug === NEW;
  const [, navigate] = useLocation();
  const { data: user, isLoading: userLoading } = useUser();
  const isAdmin = user?.role === "admin";

  const { data: post, isLoading, error } = usePost(isNew ? undefined : slug);
  const save = useSavePost();
  const remove = useDeletePost();
  const [draft, setDraft] = useState<PostInput | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const dirty = draft !== null && JSON.stringify(draft) !== saved;
  const bodyMd = (draft ?? post)?.bodyMd ?? "";
  const quotes = useMemo(() => quotedRefs(bodyMd).length > 0, [bodyMd]);

  const begin = useCallback((doc: PostInput) => {
    setDraft(doc);
    setSaved(JSON.stringify(doc));
  }, []);

  // /…/new opens straight into a blank draft.
  useEffect(() => {
    if (isNew && isAdmin && draft === null) begin(blank(section));
  }, [isNew, isAdmin, draft, begin, section]);

  useEffect(() => {
    document.title = post?.title ? `${post.title} · The Bible Unleashed` : `${name} · The Bible Unleashed`;
  }, [post?.title, name]);

  // Leaving with unsaved edits is nearly always a mistake.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const patch = useCallback((change: Partial<PostInput>) => setDraft((d) => (d ? { ...d, ...change } : d)), []);

  const stopEditing = useCallback(() => {
    if (dirty && !window.confirm("Discard the unsaved changes on this page?")) return;
    if (isNew) return navigate(home);
    setDraft(null);
    setSaved(null);
  }, [dirty, isNew, navigate, home]);

  const doSave = useCallback(
    async (status?: CommentaryStatus) => {
      if (!draft) return;
      const doc = status ? { ...draft, status } : draft;
      let result: PostView;
      try {
        result = await save.mutateAsync({ id: post?.id ?? null, doc, from: isNew ? undefined : slug });
      } catch {
        return; // the bar shows why
      }
      begin(toInput(result));
      // A new post, or a new address: the page follows the post to where it now lives.
      if (result.slug !== slug || result.section !== section) {
        navigate(`${sectionPath(result.section)}/${result.slug}`, { replace: true });
      }
    },
    [draft, save, post?.id, isNew, slug, section, begin, navigate],
  );

  const doDelete = useCallback(async () => {
    if (!post || !window.confirm(`Delete this ${noun} for good?`)) return;
    try {
      await remove.mutateAsync({ id: post.id, slug: post.slug });
    } catch {
      return;
    }
    navigate(home);
  }, [post, noun, remove, navigate, home]);

  if (isNew ? userLoading || (isAdmin && !draft) : isLoading) {
    return (
      <Shell>
        <p className="font-serif text-ink-3">Loading…</p>
      </Shell>
    );
  }
  if (error) {
    return (
      <Shell>
        <p className="font-serif text-xl">Couldn’t load this {noun}.</p>
        <p className="mt-2 text-[13px] text-ink-2">{error.message}</p>
      </Shell>
    );
  }
  // One address per post, so a link into the wrong section goes to the right one.
  if (post && !draft && post.section !== section) {
    return <Redirect to={`${sectionPath(post.section)}/${post.slug}`} replace />;
  }
  const view = draft ?? (post ? toInput(post) : null);
  if (!view) {
    return (
      <Shell>
        <p className="text-xs tracking-[0.12em] text-accent uppercase">{name}</p>
        <h1 className="mt-2 font-serif text-[36px] font-semibold">No such {noun}</h1>
        <p className="mt-3 font-serif text-lg text-ink-2">It may have moved, or not be published yet.</p>
        <Link href={home} className="mt-6 inline-block text-accent">
          Every {noun}
        </Link>
      </Shell>
    );
  }
  const editing = draft !== null;
  const hidden = view.status === "draft" || !openSections.includes(section);

  return (
    <div className="min-h-screen">
      <Header />
      <main className={`mx-auto max-w-[760px] px-6 py-14 ${isAdmin ? "pb-28" : ""}`}>
        <p className="text-xs tracking-[0.12em] uppercase">
          <Link href={home} className="text-accent">
            {name}
          </Link>
          {post?.publishedAt && (
            <span className="text-ink-3">
              {" "}
              · {section === "blog" ? postDate(post.publishedAt) : `Updated ${postDate(post.updatedAt)}`}
            </span>
          )}
        </p>
        <h1 className="mt-2 font-serif text-[38px] leading-[1.1] font-semibold tracking-[-0.015em] text-balance">
          <Editable
            label="Title"
            inline
            editing={editing}
            value={view.title}
            placeholder="Title"
            onChange={(title) => patch({ title })}
          />
        </h1>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {hidden && (
            <span className="rounded-full border border-rule px-2.5 py-0.5 font-sans text-[11px] tracking-[0.08em] text-ink-3 uppercase">
              {view.status === "draft" ? "Draft" : "Not open to readers yet"} — only you can see this
            </span>
          )}
          {editing ? (
            <TagsInput tags={view.tags} onChange={(tags) => patch({ tags })} />
          ) : (
            view.tags.map((tag) => (
              <Link
                key={tag}
                href={`${home}?tag=${encodeURIComponent(tag)}`}
                className="rounded-full bg-paper-2 px-2.5 py-0.5 font-sans text-[11px] text-ink-3 hover:text-ink"
              >
                {tag}
              </Link>
            ))
          )}
        </div>
        {editing && (
          <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_2fr]">
            <Field label={`Address — ${home}/…`} value={view.slug} placeholder="made from the title" onChange={(s) => patch({ slug: s })} />
            <Field
              label="Summary"
              value={view.excerpt}
              placeholder="For the list and the feed; blank uses the opening lines"
              onChange={(excerpt) => patch({ excerpt })}
            />
            <div className="sm:col-span-2">
              <PreviewImageField image={view.image} onChange={(image) => patch({ image })} />
            </div>
          </div>
        )}

        <div className="mt-8 font-serif text-[18px] leading-relaxed">
          <Editable
            label={name}
            editing={editing}
            value={view.bodyMd}
            placeholder={`Write the ${noun}…`}
            onChange={(md) => patch({ bodyMd: md })}
            render={(md) => <PostBody md={md} />}
          />
        </div>
        {editing && (
          <p className="mt-3 font-sans text-[12px] leading-relaxed text-ink-3">
            Markdown. A line holding only <code>{"{{Nehemiah 1:5-11}}"}</code> quotes the ESV, fetched when the {noun} is
            read; <code>[[Neh 1:5]]</code> links to a chapter; <code>[^1]</code> makes a footnote. A <code>```chart</code>{" "}
            block draws a chart: a <code>title:</code> line, then rows such as <code>Asa: 41</code> for bars,{" "}
            <code>Asa: 911–870 BC</code> for a timeline — or{" "}
            <code>word: H2617</code>, with <code>in: Psalms</code> if you like, to count a Hebrew or Greek word through the
            text. <code>note:</code> adds a line beneath; <code>unit:</code> names what the rows count.
          </p>
        )}

        {quotes && (
          <p className="mt-12 border-t border-rule pt-4 text-[11.5px] leading-normal text-ink-3">
            {ESV_COPYRIGHT}{" "}
            <a href="https://www.esv.org" target="_blank" rel="noopener noreferrer" className="text-accent">
              esv.org
            </a>
          </p>
        )}
      </main>

      {isAdmin && (
        <AdminBar
          editing={editing}
          dirty={dirty}
          status={view.status}
          saving={save.isPending}
          error={save.error?.message ?? remove.error?.message}
          exists={!isNew}
          onEdit={() => post && begin(toInput(post))}
          onDone={stopEditing}
          onSave={() => void doSave()}
          onPublish={() => void doSave(view.status === "published" ? "draft" : "published")}
          onDelete={() => void doDelete()}
        />
      )}
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-[720px] px-6 py-20">{children}</main>
    </div>
  );
}
